import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronRight,
  CircleAlert,
  FileCheck,
  Pencil,
  Trash,
  Upload,
  X,
} from 'lucide-react';

import { useModal } from '../../hooks/modalContext';
import { useEffect, useRef, useState } from 'react';
import equifax from '../../../assets/pages/creditReporting/equifax.png';
import {
  activateCreditBuilder,
  getActiveCreditBuildings,
  getTenantDetails,
  updateBuilding,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { useLeaseUpload } from '../../hooks/useLeaseUpload';
import { usePoints } from '../../hooks/pointsContext';
import SVGChecked from '../../SVG/SVGRadio/SVGChecked';
import SVGUnchecked from '../../SVG/SVGRadio/SVGUnchecked';
import { showToast } from '../../utils/showToast';
import { useNavigate } from 'react-router-dom';
import { formatApiErrorMessage } from '../../utils/error';
import {
  activateCreditBuilderBody,
  EquifaxEligibleBuilding,
  tenantDetails,
} from '../../utils/types';
import { Icon } from '../../Icons/Icon';
import { ButtonSpinner } from '../../UI/ButtonSpinner/ButtonSpinner';
import { InlineAlert } from '../../UI/InlineAlert/InlineAlert';
import DateCalendar from '../../UI/DateCalendar/DateCalendar';
import {
  FetchAddress,
  formatDateToDisplayUTC,
  keepAlphanumericAndSpace,
  parseDateStringToDate,
  standardizeDate,
} from '../../utils/utils';
import { validateEquifaxSubscriptionEligibility } from '../../utils/equifaxValidation';
import { addDays } from 'date-fns';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const CANADIAN_PROVINCES: { code: string; name: string }[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const EnableReportingModal = () => {
  const { setModal } = useModal();
  const { auth } = useAuth();
  const { refreshPoints } = usePoints();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState('');
  const [fileName, setFileName] = useState<string | undefined>('');
  const [docUrl, setdocumentUrl] = useState('');
  const [properties, setProperties] = useState<EquifaxEligibleBuilding[]>([]);
  const [propertySelectedId, setPropertySelectedId] = useState<string>('');
  const [loadingProperties, setLoadingProperties] = useState<boolean>(false);
  const [tenant, setTenant] = useState<tenantDetails>();
  const [showSteps, setShowSteps] = useState(false);
  const [activating, setActivating] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loadingTenantDetails, setLoadingTenantDetails] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const formik = useFormik({
    initialValues: {
      leaseStart: '',
      leaseEnd: '',
      address: '',
      house: '',
      street: '',
      city: '',
      province: '',
      provinceCode: '',
      country: 'Canada',
      countryCode: 'CA',
      postal: '',
      unitNumber: '',
    },
    validationSchema: Yup.object({
      address: Yup.string().required('Address is required'),
      house: Yup.string().required('Building/Street number is required'),
      street: Yup.string().required('Street name is required'),
      city: Yup.string().required('City is required'),
      province: Yup.string().required('Province is required'),
      provinceCode: Yup.string().required('Province Code is required'),
      postal: Yup.string().required('Postal code is required'),
      unitNumber: Yup.string().required('Unit number is required'),
    }),
    onSubmit: () => {
      // Form is not submitted directly by Formik; submit is handled by handleSubmit function.
    },
  });
  useEffect(() => {
    const field = 'house';
    const value = formik.values[field];

    // Manually validate the field
    formik.validateField(field).then(() => {
      const error = formik.errors[field];
      if (!error && value) {
        formik.setFieldTouched(field, true, false);
      }
    });
  }, [currentStep]);

  const { isUploading, progress, handleFileChange } = useLeaseUpload({
    token: auth?.data.token,
    onUploaded: ({ url, downloadUrl, fileName: uploadedName }) => {
      setFileName(uploadedName);
      setdocumentUrl(downloadUrl);
      setSelectedFile(url);
    },
  });

  useEffect(() => {
    const selectedProperty = properties.find((p) => p.tenantBuildingId === propertySelectedId);
    setSelectedFile(selectedProperty?.leaseDocumentUrl || '');
    setFileName(
      selectedProperty?.leaseDocumentUrl ? selectedProperty?.leaseDocumentUrl.split('/').pop() : ''
    );
    const formattedDate = selectedProperty?.leaseStartDate
      ? formatDateToDisplayUTC(selectedProperty.leaseStartDate)
      : '';
    const endDate = selectedProperty?.leaseEndDate
      ? formatDateToDisplayUTC(selectedProperty.leaseEndDate)
      : '';
    const parsedDate = selectedProperty?.leaseStartDate
      ? parseDateStringToDate(formattedDate)
      : new Date();
    setStartDate(parsedDate);
    const parsedEndDate = selectedProperty?.leaseEndDate
      ? parseDateStringToDate(endDate)
      : new Date();
    setEndDate(parsedEndDate);
    formik.setValues({
      leaseStart: formattedDate ?? '',
      leaseEnd: endDate ?? '',
      address: selectedProperty?.fullAddress || '',
      house: selectedProperty?.buildingNumber || '',
      street: selectedProperty?.streetName || '',
      city: selectedProperty?.city || '',
      province: selectedProperty?.province || '',
      provinceCode: selectedProperty?.provinceCode || '',
      country: selectedProperty?.country || 'Canada',
      countryCode: selectedProperty?.countryCode || 'CA',
      postal: selectedProperty?.postalCode || '',
      unitNumber: selectedProperty?.unitNumber ?? '',
    });
    setStartDate(
      selectedProperty?.leaseStartDate ? new Date(selectedProperty?.leaseStartDate) : null
    );
    setEndDate(selectedProperty?.leaseEndDate ? new Date(selectedProperty?.leaseEndDate) : null);
    setdocumentUrl(selectedProperty?.leaseDocumentDownloadUrl || '');
  }, [propertySelectedId, properties]);
  const openDocument = () => {
    if (selectedFile) {
      window.open(docUrl, '_blank');
    }
  };
  const handlePropertySelect = (id: string) => {
    const newValue = propertySelectedId === id ? '' : id;
    setPropertySelectedId(newValue);
  };
  const steps = [
    {
      component: (
        <div className='flex flex-col items-start justify-center h-full gap-8 px-10 mb-40'>
          <div className='flex flex-col gap-1'>
            <span className='text-main font-bold text-[18px]'>Get started with Credit Builder</span>
            <span className='text-main text-base'>
              Credit Builder is a free benefit for TenantPay users that lets you automatically
              report rent payments to Equifax, helping you build credit.
            </span>
          </div>

          <div className='flex gap-3'>
            <span className='text-light/70 italic'>Powered by</span>
            <img src={equifax} alt='Credit Builder Logo' className='w-24 h-6 object-contain' />
          </div>

          <div className='flex flex-col items-start gap-6 text-base'>
            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>No additional cost</span>
              <span className='text-main'>
                Credit Builder is available at no cost for TenantPay users who have active rent
                payments.
              </span>
            </div>

            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>Build credit without debt</span>
              <span className='text-main'>
                Boost your credit score with on-time rent payments, without taking on new debt.
              </span>
            </div>

            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>Strengthen your next rental application</span>
              <span className='text-main'>
                Provide future landlords with a verified rent payment history.
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      component: (
        <div className='flex flex-col items-start justify-center h-full gap-8 px-10 mb-40'>
          <div className='space-y-4'>
            <span className='text-main font-bold text-[18px]'>
              Credit Builder terms & conditions
            </span>
          </div>

          <div className='flex flex-col items-start gap-6 text-base'>
            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>Lease verification</span>
              <span className='text-main'>
                TenantPay needs to verify your lease agreement before we can start reporting your
                rent. If there are any issues, we will let you know what needs to be updated.
              </span>
            </div>

            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>Information we share</span>
              <span className='text-main'>
                We share your full name, date of birth, phone number, full address, and details
                about your rent payments. Your data is secure with us. We share your information
                ONLY with Equifax for rent reporting purposes. You can learn more by visiting
                Equifax’s Privacy Policy.
              </span>
            </div>

            <div className='flex flex-col items-start gap-2'>
              <span className='text-main'>
                Your data is secure with us. We share your information ONLY with Equifax for rent
                reporting purposes. You can learn more by visiting Equifax's Privacy Policy.
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      component: (
        <div className='flex flex-col items-start justify-center h-full gap-8 px-10 mb-40'>
          <div className='space-y-4'>
            <span className='text-main font-bold block text-[18px]'>How it works</span>
            <span className='text-main block text-base'>
              Here's how the process works and what you need to know.
            </span>
          </div>

          <div className='flex flex-col items-start gap-6 text-base'>
            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>📤 Upload your lease</span>
              <span className='text-main'>
                Upload your lease so we can verify your rental address for credit reporting.
              </span>
            </div>

            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>🚫 No landlord contact needed</span>
              <span className='text-main'>
                We do not need to contact your landlord or property manager.
              </span>
            </div>
            <div className='flex flex-col items-start gap-2'>
              <span className='text-main font-bold'>⚠️ Cancel with caution</span>
              <span className='text-main'>
                If you cancel Credit Builder, you won’t be able to reactivate it at the same
                address.
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      component: (
        <div className='flex flex-col items-start justify-center h-full gap-8 px-10'>
          <div className='space-y-4'>
            <span className='text-main font-bold block text-[18px]'>Upload your lease</span>
            <span className='text-main block text-base'>
              We know this step can be a bit of a pain, but Equifax requires TenantPay to validate
              your lease before we can start reporting. We can't begin until we have a valid lease
              agreement, including the lease start and end dates.
            </span>
          </div>

          <div className='flex flex-col items-start gap-6'>
            <div className={'inputWrapper'}>
              {!selectedFile ? (
                <>
                  <div className={'label'}>
                    Upload file<span>*</span>
                  </div>
                  <div
                    className={'inputField flex gap-3 cursor-pointer'}
                    onClick={() => {
                      if (fileInputRef.current !== undefined) {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <input
                      onChange={handleFileChange}
                      multiple={false}
                      ref={fileInputRef}
                      type={selectedFile === '' ? 'file' : ''}
                      hidden
                      accept='.pdf'
                    />
                    <div className='flex items-center gap-2'>
                      <Upload className='text-gray-500 h-5 w-5' />
                      <span className='text-light/70'>Choose a file</span>
                    </div>
                  </div>
                  {isUploading && <div>Uploading... {progress}%</div>}
                  <span className='text-[#8A92AB] text-xs'>We support PDF documents only</span>
                </>
              ) : (
                <>
                  <div className={'inputField flex gap-3 cursor-pointer'}>
                    <div className='flex items-center gap-2'>
                      <FileCheck className='text-gray-500 h-5 w-5' />
                      <span
                        className='text-light/70'
                        onClick={(e) => {
                          e.preventDefault();
                          openDocument();
                        }}
                      >
                        {fileName}
                      </span>
                      {selectedFile && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedFile('');
                          }}
                        >
                          <Trash className='text-gray-500 h-5 w-5' />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className='flex w-full gap-5'>
              <div className='inputWrapper'>
                <div className={'label'}>
                  Lease start date<span>*</span>
                </div>
                <div className={'inputField '}>
                  <DateCalendar
                    startDate={startDate}
                    maxDate={addDays(endDate as Date, -1)}
                    onDateChange={setStartDate}
                    formik={formik}
                    type='leaseStart'
                    placement='topEnd'
                  />
                </div>
              </div>
              <div className='inputWrapper'>
                <div className={'label'}>
                  Lease end date<span>*</span>
                </div>
                <div className={'inputField '}>
                  <DateCalendar
                    startDate={endDate}
                    minDate={addDays(startDate as Date, 1)}
                    onDateChange={setEndDate}
                    formik={formik}
                    type='leaseEnd'
                    placement='topEnd'
                  />
                </div>
              </div>
            </div>
            <>
              <span className='text-main block text-base mt-10'>
                All the address fields are required to proceed with rent reporting.
              </span>
              <div className='flex w-full flex-col mt-4'>
                <div className='flex items-center justify-between gap-2 mb-2'>
                  <div className={'label-light'}>
                    Address<span>*</span>
                  </div>
                  {!isManualAddress && (
                    <button
                      type='button'
                      className='flex items-center gap-1.5 text-sm text-primary hover:underline'
                      onClick={() => setIsManualAddress(true)}
                    >
                      <Pencil className='h-4 w-4' />
                      <span>Enter manually</span>
                    </button>
                  )}
                </div>
                {!isManualAddress ? (
                  <>
                    <div className={'inputWrapper w-full'}>
                      <div className={'inputField '}>
                        <input
                          ref={addressInputRef}
                          type='text'
                          autoComplete='off'
                          {...formik.getFieldProps('address')}
                          className='text-main'
                          placeholder='Enter your full address'
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                            }
                          }}
                        />
                      </div>
                      {formik.touched.address && formik.errors.address && (
                        <div className='error'>{formik.errors.address}</div>
                      )}
                    </div>
                    <div className={'inputWrapper w-full mt-4'}>
                      <div className={'label-light'}>
                        Unit number<span>*</span>
                      </div>
                      <div className={'inputField '}>
                        <input
                          type='text'
                          autoComplete='off'
                          {...formik.getFieldProps('unitNumber')}
                          className='text-main'
                          placeholder='Enter unit number'
                          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                        />
                      </div>
                      {formik.touched.unitNumber && formik.errors.unitNumber && (
                        <div className='error'>{formik.errors.unitNumber}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className='flex flex-col gap-6 mt-4'>
                    <button
                      type='button'
                      className='self-start flex items-center gap-1 text-sm text-light/70 hover:text-main mb-2'
                      onClick={() => setIsManualAddress(false)}
                    >
                      <ChevronRight className='h-4 w-4 rotate-180' />
                      <span>Back to address search</span>
                    </button>
                    <div className='flex gap-5 w-full flex-wrap'>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Building/street number<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('house')}
                            placeholder='Enter building/street number'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.house && formik.errors.house && (
                          <div className='error'>{formik.errors.house}</div>
                        )}
                      </div>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Unit number<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('unitNumber')}
                            placeholder='Enter unit number'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.unitNumber && formik.errors.unitNumber && (
                          <div className='error'>{formik.errors.unitNumber}</div>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-5 w-full flex-wrap'>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Street name<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('street')}
                            placeholder='Enter your street name'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.street && formik.errors.street && (
                          <div className='error'>{formik.errors.street}</div>
                        )}
                      </div>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          City<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('city')}
                            placeholder='Enter your city'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.city && formik.errors.city && (
                          <div className='error'>{formik.errors.city}</div>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-5 w-full flex-wrap'>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Province<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('province')}
                            placeholder='Enter your province'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.province && formik.errors.province && (
                          <div className='error'>{formik.errors.province}</div>
                        )}
                      </div>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Province code<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <select
                            id='provinceCode'
                            className='text-main w-full bg-transparent border-none outline-none'
                            value={formik.values.provinceCode}
                            onChange={(e) => {
                              const option = CANADIAN_PROVINCES.find(
                                (p) => p.code === e.target.value
                              );
                              if (option) {
                                formik.setFieldValue('province', option.name);
                                formik.setFieldValue('provinceCode', option.code);
                              }
                            }}
                            onBlur={() => formik.setFieldTouched('provinceCode', true)}
                          >
                            <option value=''>Select province</option>
                            {CANADIAN_PROVINCES.map((p) => (
                              <option key={p.code} value={p.code}>
                                {p.code} – {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formik.touched.provinceCode && formik.errors.provinceCode && (
                          <div className='error'>{formik.errors.provinceCode}</div>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-5 w-full flex-wrap'>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>
                          Postal code<span>*</span>
                        </div>
                        <div className={'no-border-input-field'}>
                          <input
                            type='text'
                            autoComplete='off'
                            {...formik.getFieldProps('postal')}
                            placeholder='Enter postal code'
                            className='text-main'
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          />
                        </div>
                        {formik.touched.postal && formik.errors.postal && (
                          <div className='error'>{formik.errors.postal}</div>
                        )}
                      </div>
                      <div className={'inputWrapper flex-1 min-w-[140px]'}>
                        <div className={'label-light'}>Country</div>
                        <div className={'no-border-input-field'}>
                          <span className='text-main'>Canada</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          </div>
        </div>
      ),
    },
  ];
  const slideVariants = {
    enter: {
      y: -20,
      opacity: 0,
    },
    center: {
      y: 0,
      opacity: 1,
    },
    exit: {
      y: 20,
      opacity: 0,
      scale: 0.98, // slight shrink to enhance morph feel
    },
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };
  const fetchTenantDetails = async () => {
    try {
      setLoadingTenantDetails(true);
      const response = await getTenantDetails(auth?.data.token);

      setTenant(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    } finally {
      setLoadingTenantDetails(false);
    }
  };
  useEffect(() => {
    fetchTenantDetails();
  }, []);

  const handleSubmit = async () => {
    const validation = validateEquifaxSubscriptionEligibility(tenant);
    if (!validation.isValid) {
      if (validation.errorType === 'idv_required') {
        showToast({
          message: validation.errorMessage || 'Please complete Identity Verification (IDV) first.',
          type: 'error',
        });
        setModal('identity-verification');
      } else if (validation.errorType === 'profile_incomplete') {
        showToast({
          message: validation.errorMessage || 'Please complete your profile before subscribing.',
          type: 'error',
        });
      }
      return;
    }

    const requestBodyBuilding = {
      buildingDetails: {
        leaseDocumentUrl: selectedFile,
        leaseStartDate: startDate ? standardizeDate(formik.values.leaseStart) : '',
        leaseEndDate: endDate ? standardizeDate(formik.values.leaseEnd) : '',
      },
      addressDetails: {
        fullAddress: formik.values.address || '',
        streetName: formik.values.street || '',
        city: formik.values.city || '',
        province: formik.values.province || '',
        provinceCode: formik.values.provinceCode || '',
        postalCode: formik.values.postal || '',
        buildingNumber: formik.values.house || '',
        unitNumber: formik.values.unitNumber || '',
      },
    };
    await updateBuilding(auth?.data.token, propertySelectedId, requestBodyBuilding);

    const requestBody: activateCreditBuilderBody = {
      surname: tenant?.user.lastName || '',
      firstName: tenant?.user.firstName || '',
      middleName: tenant?.user.middleName || '',
      dateBirth: tenant?.user.dateOfBirth || '',
      telephoneNumber: tenant?.user.phoneNumber || '',
      address: {
        fullAddress: keepAlphanumericAndSpace(formik.values.address) || '',
        streetName: keepAlphanumericAndSpace(formik.values.street) || '',
        city: keepAlphanumericAndSpace(formik.values.city) || '',
        province: keepAlphanumericAndSpace(formik.values.province) || '',
        provinceCode: keepAlphanumericAndSpace(formik.values.provinceCode) || '',
        country: keepAlphanumericAndSpace(formik.values.country) || '',
        countryCode: keepAlphanumericAndSpace(formik.values.countryCode || 'CA'),
        postalCode: keepAlphanumericAndSpace(formik.values.postal) || '',
        buildingNumber: keepAlphanumericAndSpace(formik.values.house) || '',
        unitNumber: keepAlphanumericAndSpace(formik.values.unitNumber) || '',
      },
    };
    try {
      setActivating(true);
      const response = await activateCreditBuilder(
        auth?.data.token,
        propertySelectedId,
        requestBody
      );
      if (response.message === 'success') {
        await refreshPoints();
        setModal('successful-equifax-reporting', {
          refreshEquifaxEligibleBuildings: true, // Reset the flag after refreshing
        });
        showToast({
          message: 'Credit Builder has been successfully activated for the selected property!',
          type: 'success',
        });

        setTimeout(() => {
          navigate('/dashboard');
        }, 7000);
      } else if (response.message === 'already_subscribed') {
        void refreshPoints().catch((err) => console.error('Failed to refresh points', err));
        showToast({
          message: 'Credit Builder has already been activated for the selected property.',
          type: 'success',
        });

        setTimeout(() => {
          navigate('/dashboard');
        }, 7000);
      } else {
        throw new Error(
          formatApiErrorMessage(response.message, 'Failed to activate Credit Builder reporting. Please try again.')
        );
      }
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to activate Credit Builder reporting. Please try again.';
      setServerError(msg);
      showToast({
        message: msg,
        type: 'error',
      });
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    const fetchBuildings = async () => {
      setLoadingProperties(true);
      try {
        const res = await getActiveCreditBuildings(auth?.data.token);

        if (res?.data) {
          setProperties(res.data);
        }
      } catch (err) {
        console.error('Error fetching buildings:', err);
      } finally {
        setLoadingProperties(false);
      }
    };

    fetchBuildings();
  }, [auth?.data?.token]);

  if (loadingProperties || loadingTenantDetails) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
      </div>
    );
  }

  return (
    <motion.div
      className='relative w-[554px] min-w-[471px] min-h-[300px] bg-white rounded-[12px] flex flex-col max-h-[95vh] overflow-hidden max-w-[90vw]'
      initial='enter'
      animate='center'
      exit='exit'
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>
      {tenant?.user?.phoneNumber ? (
        properties.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full px-10 text-center gap-10'>
            <CircleAlert className='text-orange-500 w-12 h-12' />
            <p className='text-main font-medium text-lg -mt-5'>
              To start Credit Builder, you must have at least one property with an active recurring
              payment.
            </p>
            <p className='text-light/70 text-sm'>
              Please go to your dashboard and start a recurring rent payment to proceed.
              <br />
              Also, please note that you cannot subscribe again to any building that was previously
              unsubscribed from Credit Builder.
            </p>
            <button
              className='submitBtnGreen mb-10'
              onClick={() => {
                setModal('select-property', {
                  paymentType: 'subscription',
                  fromModal: 'enable-reporting',
                });
              }}
            >
              <div className='flex justify-between w-full px-6 '>
                <span className='font-semibold'>Set up autopay</span>
                <Icon name='refreshDouble' className='text-white h-5 w-5' />
              </div>
            </button>
          </div>
        ) : !showSteps ? (
          <div className='flex flex-col justify-start items-start h-full px-10'>
            <div className='flex-1 flex items-start justify-start '>
              {loadingProperties ? (
                <div className='flex justify-center items-center w-full'>
                  <div className='w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                </div>
              ) : (
                properties.length !== 0 && (
                  <div className='flex flex-col items-start justify-between gap-5 w-full h-full'>
                    <span className='text-main text-[20px]'>Select property</span>
                    <span className='text-light/80 text-[15px]'>
                      Select the property below that you would like to enable Rent Reporting for:
                    </span>
                    {properties.map((property) => (
                      <div
                        className='flex w-full items-center gap-2 p-4 cursor-pointer border border-[rgba(63,68,83,0.20)] rounded-[8px]'
                        key={property?.tenantBuildingId}
                        onClick={() => handlePropertySelect(property?.tenantBuildingId)}
                      >
                        {propertySelectedId === property?.tenantBuildingId ? (
                          <SVGChecked />
                        ) : (
                          <SVGUnchecked />
                        )}
                        <div className='flex flex-col gap-1'>
                          <span className='text-main font-semibold text-base'>
                            {property?.name}
                          </span>
                          <span className='text-light/70 text-sm'>{property?.fullAddress}</span>
                        </div>
                      </div>
                    ))}
                    <button
                      className='submitBtn w-full mt-auto mb-8'
                      onClick={() => setShowSteps(true)}
                      disabled={!propertySelectedId}
                    >
                      <div className='flex p-4 justify-between w-full'>
                        <span>Next</span>
                        <ArrowRight />
                      </div>
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        ) : (
          // Replace your showSteps === true block with this
          <div className='flex flex-col h-full max-h-[calc(95vh-65px)]'>
            {/* Header with title + progress bar */}
            <div className='shrink-0 px-10'>
              <span className='text-main font-normal text-[20px]'>Enable reporting</span>
              <div className='flex items-center justify-between w-full pt-4'>
                {Array.from({ length: steps.length }).map((_, index) => (
                  <div
                    key={index}
                    className={`h-1 flex-1 mx-1 rounded-sm ${
                      index <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Scrollable content */}
            <div
              className={`flex-1 overflow-y-auto mt-10 ${isAutoCompleteOpen ? 'overflow-hidden' : ''}`}
            >
              {currentStep === 3 && (
                <FetchAddress
                  addressInputRef={addressInputRef}
                  formik={formik}
                  isSaveManually={setIsManualAddress}
                  setIsAutocompleteOpen={setIsAutoCompleteOpen}
                />
              )}
              <AnimatePresence mode='wait' custom={currentStep}>
                <motion.div
                  key={currentStep}
                  custom={currentStep}
                  variants={slideVariants}
                  initial='enter'
                  animate='center'
                  exit='exit'
                  transition={{ duration: 0.3 }}
                  className='w-full'
                >
                  {steps[currentStep].component}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Optional error alert */}
            {serverError && (
              <div className='px-8 mt-2 shrink-0'>
                <InlineAlert
                  type='error'
                  message={serverError}
                  dismissible
                  onDismiss={() => setServerError('')}
                />
              </div>
            )}

            {/* Fixed footer button */}
            <div className='p-6 shrink-0'>
              {currentStep === steps.length - 1 ? (
                <button
                  className='submitBtn w-full'
                  onClick={handleSubmit}
                  disabled={
                    !selectedFile ||
                    !formik.values.leaseEnd ||
                    !formik.values.leaseStart ||
                    !formik.values.address ||
                    !formik.values.house ||
                    !formik.values.street ||
                    !formik.values.city ||
                    !formik.values.province ||
                    !formik.values.provinceCode ||
                    !formik.values.postal ||
                    !formik.values.unitNumber ||
                    activating
                  }
                >
                  {activating && <ButtonSpinner />}
                  Activate
                </button>
              ) : (
                <button className='submitBtn flex items-center w-full' onClick={nextStep}>
                  <div className='flex text-base justify-between w-full items-center p-6'>
                    {currentStep === 2 ? <span>Next</span> : <span>Accept and continue</span>}
                    <ArrowRight />
                  </div>
                </button>
              )}
            </div>
          </div>
        )
      ) : (
        <div className='flex flex-col items-center justify-center h-full px-10 text-center gap-10'>
          <CircleAlert className='text-orange-500 w-10 h-10 -mt-5' />
          <p className='text-main font-medium text-lg'>
            To start Credit Builder, please verify your phone number in your profile.
          </p>
          <p className='text-light/70 text-sm'>
            Go to your profile settings and verify your phone number to continue.
          </p>
          <button
            className='submitBtnGreen mb-10'
            onClick={() => {
              setModal('profile');
            }}
          >
            <div className='flex justify-between w-full px-6'>
              <span className='font-semibold'>Add and verify phone number</span>
              <ChevronRight name='refreshDouble' className='text-white h-5 w-5' />
            </div>
          </button>
        </div>
      )}
    </motion.div>
  );
};

export { EnableReportingModal };
